import React, { useState, useEffect, useCallback } from 'react';
import { useForm, FormProvider, UseFormRegister, UseFormWatch, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Tenant, TenantFormData, TenantLevel, TenantType } from '../../types';
import EmailConfigList from '../email/EmailConfigList';
import { EmailConfig, getEmailConfigs } from '../../services/emailConfigService';
import { getTenants, getTenant } from '../../services/api';
import { 
  ArrowRight, 
  ArrowLeft, 
  Check, 
  Calendar, 
  Database, 
  Link, 
  Mail, 
  Server, 
  Shield,
  XCircle,
  ClipboardCheck,
  Building,
  FileText,
  CheckSquare,
  Eye,
  EyeOff,
  Copy,
  CheckCheck,
  ImagePlus,
  X,
  ChevronDown,
  Loader2
} from 'lucide-react';
import ImageUploadModal from '../tenants/ImageUploadModal';
import MultipleDocumentUpload from '../tenants/MultipleDocumentUpload';
import { getCountries, getCitiesForCountry, getIndustries, Country, City, Industry } from '../../services/locationApi';

// Lightweight in-memory cache to avoid repeated network calls per form open
const cachedParentTenantsByLevel: Record<string, Tenant[]> = {};
let cachedCountries: Country[] | null = null;
let cachedIndustries: Industry[] | null = null;

// Define SOWDocument interface locally
interface SOWDocument {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  created_at: string;
}

// Define the schema type to match TenantFormData
const tenantFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name must be at most 100 characters'),
  tenant_code: z.string().optional(),
  uuid: z.string().optional(),
  tenant_type: z.string(),
  level: z.string(),
  logo_url: z.string().nullable().optional(),
  sow_document_url: z.string().nullable().optional(), // Added SOW document URL field
  sow_documents: z.array(z.object({
    id: z.string(),
    name: z.string(),
    url: z.string(),
    type: z.string(),
    size: z.number(),
    created_at: z.string()
  })).optional().default([]),
  group: z.string().optional().or(z.literal('')), // Make group field truly optional
  parent_tenant_id: z.string().optional(),
  status: z.string(),
  // Use string preprocessing for date fields to convert empty string to null
  start_date: z.preprocess(
    (val) => val === "" ? null : val,
    z.string().nullable().optional()
  ),
  end_date: z.preprocess(
    (val) => val === "" ? null : val,
    z.string().nullable().optional()
  ),
  onboarding_date: z.preprocess(
    (val) => val === "" ? null : val,
    z.string().nullable().optional()
  ),
  contract_renewal_status: z.preprocess(
    (val) => val === "" ? null : val,
    z.string().nullable().optional()
  ),
  threshold_per_day: z.preprocess(
    (val) => (val === "" || val === null || val === undefined || Number.isNaN(Number(val))) ? null : Number(val),
    z.number().nullable().optional()
  ),
  online_retention: z.preprocess(
    (val) => (val === "" || val === null || val === undefined || Number.isNaN(Number(val))) ? null : Number(val),
    z.number().nullable().optional()
  ),
  offline_retention: z.preprocess(
    (val) => (val === "" || val === null || val === undefined || Number.isNaN(Number(val))) ? null : Number(val),
    z.number().nullable().optional()
  ),
  email_to: z.string().optional(),
  email_cc: z.string().optional(),
  from_email: z.string().optional(),
  soar_api_key: z.string().optional(),
  soar_api_url: z.string().optional(),
  soar_tenant_id: z.string().optional(),
  vision_one_url: z.string().optional(),
  vision_one_api_key: z.string().optional(),
  destination: z.string().optional(),
  provider_name: z.string().optional(),
  scan_action: z.string().optional().or(z.literal('')), // Make scan_action field truly optional
  attached_ioc: z.boolean().default(false),
  masking_ioc: z.boolean().default(false),
  sweeping_on_vision_one: z.boolean().default(false),
  sweeping_on_esdl: z.boolean().default(false),
  country: z.string().optional().or(z.literal('')),
  industry: z.array(z.string()).optional().default([]),
  head_quarter: z.string().optional().or(z.literal('')),
  asset_management_iocs: z.array(z.object({
    type: z.string(),
    value: z.string(),
    action: z.string()
  })).optional().default([])
});

// Extract the inferred type
type TenantFormSchema = z.infer<typeof tenantFormSchema>;

// Common props interface for all step components
interface StepComponentProps {
  register: UseFormRegister<TenantFormSchema>;
  errors: FieldErrors<TenantFormSchema>;
  watch?: UseFormWatch<TenantFormSchema>;
}

// Additional props for steps that need watch functionality
interface WatchableStepComponentProps extends StepComponentProps {
  watch: UseFormWatch<TenantFormSchema>;
}

// Function to generate tenant code from name
const generateTenantCode = (name: string): string => {
  if (!name) return '';
  
  // Split the name by spaces to get words
  const words = name.split(/\s+/);
  
  if (words.length === 1) {
    // If single word, take up to first 4 letters uppercase
  const cleanedName = name.replace(/[^a-zA-Z]/g, '');
  return cleanedName.substring(0, 4).toUpperCase();
  } else {
    // If multiple words, take first letter of each word (up to 4)
    let code = '';
    for (let i = 0; i < Math.min(words.length, 4); i++) {
      if (words[i] && words[i].length > 0) {
        // Get first letter of each word
        code += words[i][0];
      }
    }
    
    // If we don't have 4 characters yet, add from the first word
    if (code.length < 4 && words[0].length > 1) {
      code += words[0].substring(1, 5 - code.length);
    }
    
    return code.toUpperCase();
  }
};

// CSS and style enhancements - let's apply these consistently
const selectStyle = {
  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
  backgroundPosition: `right 0.5rem center`,
  backgroundSize: `1.5em 1.5em`,
  backgroundColor: `rgba(30, 41, 59, 0.8)` // Darker blue-gray background
};

// Update the form field styling
const formFieldStyle = "w-full bg-slate-800/80 border border-primary/30 text-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary/50 focus:outline-none transition-all duration-200";

// Custom Select Component to ensure consistent styling
const PremiumSelect = ({ 
  id, 
  label, 
  required = false, 
  register, 
  name, 
  children, 
  error,
  disabled = false,
  loading = false
}: { 
  id: string; 
  label: string; 
  required?: boolean; 
  register: any; 
  name: string; 
  children: React.ReactNode; 
  error?: any;
  disabled?: boolean;
  loading?: boolean;
}) => {
  return (
    <div className="form-group mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1.5">
        {label} {required && <span className="text-primary">*</span>}
        {loading && (
          <span className="ml-2 inline-block w-4 h-4 border-2 border-t-transparent border-primary rounded-full animate-spin"></span>
        )}
      </label>
      <div className="relative">
      <select
        id={id}
          className={`${formFieldStyle} appearance-none ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
        style={{
          ...selectStyle,
        }}
        disabled={disabled || loading}
        {...register(name)}
      >
        {children}
      </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
      {error && <p className="text-red-400 text-xs mt-1">{error.message}</p>}
    </div>
  );
};

// Multi-email input component
interface MultiEmailInputProps {
  id: string;
  label: string;
  placeholder: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  error?: any;
}

const MultiEmailInput: React.FC<MultiEmailInputProps> = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  error
}) => {
  const [inputValue, setInputValue] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  
  // Parse initial value on component mount or when value changes
  useEffect(() => {
    if (value) {
      const emailArray = value.split(',')
        .map(email => email.trim())
        .filter(email => email !== '');
      
      setEmails(emailArray);
    } else {
      setEmails([]);
    }
  }, [value]);
  
  // Update parent form when emails change
  const updateEmails = (newEmails: string[]) => {
    setEmails(newEmails);
    onChange(newEmails.join(','));
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', 'Tab', ',', ';'].includes(e.key)) {
      e.preventDefault();
      addEmail();
    }
  };
  
  const addEmail = () => {
    const trimmedInput = inputValue.trim();
    
    if (trimmedInput) {
      // Basic email validation
      const isValidEmail = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(trimmedInput);
      
      if (isValidEmail && !emails.includes(trimmedInput)) {
        updateEmails([...emails, trimmedInput]);
        setInputValue('');
      }
    }
  };
  
  const removeEmail = (index: number) => {
    const newEmails = [...emails];
    newEmails.splice(index, 1);
    updateEmails(newEmails);
  };
  
  const handleBlur = () => {
    addEmail();
  };
  
  return (
    <div className="form-group mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-gray-400 mb-1.5">{label}</label>
      <div className="relative">
        <div className="flex flex-wrap gap-2 p-2 bg-slate-800/50 border border-primary/30 rounded-lg text-gray-200 min-h-[42px] focus-within:ring-2 focus-within:ring-primary/50 focus-within:border-primary/50 transition-all duration-200">
          {emails.map((email, index) => (
            <div 
              key={index} 
              className="flex items-center gap-1 bg-gradient-to-b from-slate-700 to-slate-800 text-gray-300 px-2 py-1 rounded text-sm border border-primary/20"
            >
              <span>{email}</span>
              <button 
                type="button" 
                onClick={() => removeEmail(index)}
                className="text-gray-400 hover:text-gray-200 transition-colors"
              >
                <XCircle size={14} />
              </button>
            </div>
          ))}
          <input
            id={id}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="flex-grow min-w-[150px] bg-transparent border-0 focus:ring-0 p-1 text-gray-200 placeholder-gray-500 outline-none"
            placeholder={emails.length === 0 ? placeholder : "Add another email..."}
          />
        </div>
        <div className="text-xs text-gray-500 mt-1 ml-1">Press Enter, Tab, or comma to add email</div>
      </div>
      {error && <p className="text-red-400 text-xs mt-1">{error.message}</p>}
    </div>
  );
};

// Step 1: Basic Configuration
type TenantFormContext = Partial<TenantFormSchema> & { is_deleted?: boolean };

const BasicConfigStep = ({ register, watch, errors, setValue, tenant }: WatchableStepComponentProps & { setValue: any; tenant?: TenantFormContext }) => {
  const [parentTenants, setParentTenants] = useState<Tenant[]>([]);
  const [isLoadingParents, setIsLoadingParents] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const logoUrl = watch('logo_url');
  const [logoPreview, setLogoPreview] = useState<string | undefined>(logoUrl || undefined);
  
  // Update logo preview when logo_url changes
  useEffect(() => {
    setLogoPreview(logoUrl || undefined);
  }, [logoUrl]);
  
  // Dynamic dropdown states
  const [countries, setCountries] = useState<Country[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isLoadingIndustries, setIsLoadingIndustries] = useState(false);
  const [isManualInputMode, setIsManualInputMode] = useState(false);
  
  const tenantType = watch('tenant_type');
  const tenantLevel = watch('level');
  const tenantName = watch('name');
  const parentTenantId = watch('parent_tenant_id');
  const selectedCountry = watch('country');
  const selectedIndustries = watch('industry') || [];
  const selectedHeadQuarter = watch('head_quarter');
  
  // Log when parentTenantId changes
  useEffect(() => {
    // Parent tenant ID updated
  }, [parentTenantId]);
  
  // Generate tenant code when name changes
  useEffect(() => {
    if (tenantName && !watch('tenant_code')) {
      const generatedCode = generateTenantCode(tenantName);
      setValue('tenant_code', generatedCode);
    }
  }, [tenantName, setValue, watch]);
  
  // Load parent tenants immediately on component mount if we have a parentTenantId
  useEffect(() => {
    const loadParentTenant = async () => {
      if (parentTenantId) {
        try {
          setIsLoadingParents(true);
          const parent = await getTenant(parentTenantId);
          if (parent) {
            setParentTenants([parent]);
          }
        } catch (error) {
          console.error('Error fetching specific parent tenant:', error);
        } finally {
          setIsLoadingParents(false);
        }
      }
    };
    
    loadParentTenant();
  }, [parentTenantId]); // Add parentTenantId as dependency to reload when it changes
  
  // Make sure tenantType and tenantLevel are in sync
  useEffect(() => {
    // If tenant type changes, ensure level is compatible
    if (tenantType === 'partner' && tenantLevel === 'L3') {
      setValue('level', '');
    } else if (tenantType === 'org' && (tenantLevel === 'L1' || tenantLevel === 'L2')) {
      setValue('level', '');
    } else if (tenantType === 'partner' && !tenantLevel) {
      // Default level for partner with no level set
      setValue('level', 'L1');
    } else if (tenantType === 'org' && !tenantLevel) {
      // Default level for org with no level set
      setValue('level', 'L3');
    }
  }, [tenantType, tenantLevel, setValue]);
  
  const fetchParentTenants = useCallback(async () => {
    // Only fetch parent tenants for non-L0 tenants
    if (tenantLevel === 'L0') {
      setParentTenants([]);
      return;
    }
    
    // Serve instantly if cached
    if (tenantLevel && cachedParentTenantsByLevel[tenantLevel]) {
      setParentTenants(cachedParentTenantsByLevel[tenantLevel]);
    } else {
      setIsLoadingParents(true);
    }
    
    try {
      // Determine which levels can be parents based on current level
      let parentLevels = '';
      
      if (tenantLevel === 'L1') {
        parentLevels = 'L0';
      } else if (tenantLevel === 'L2') {
        parentLevels = 'L0,L1';
      } else if (tenantLevel === 'L3') {
        parentLevels = 'L0,L1,L2';
      }
      
      let tenants = cachedParentTenantsByLevel[tenantLevel || ''] || [];
      if (tenants.length === 0) {
        tenants = await getTenants({ level: parentLevels, status: 'active' });
        cachedParentTenantsByLevel[tenantLevel || ''] = tenants;
      }
      
      if (parentTenantId) {
        const parentExists = tenants.some((t) => t.id === parentTenantId);
        
        if (!parentExists) {
          if (tenant && tenant.parent_tenant_id === parentTenantId) {
            tenants.push({
              id: tenant.parent_tenant_id as any,
              name: (tenant as any).parent_tenant_name || 'Parent',
              level: 'L0'
            } as unknown as Tenant);
          } else {
            try {
              const parentTenant = await getTenant(parentTenantId);
              if (parentTenant) {
                tenants.push(parentTenant);
              }
            } catch (error) {
              console.error('Error fetching specific parent tenant:', error);
            }
          }
        }
      }
      
      setParentTenants(tenants);
    } catch (error) {
      console.error('Error fetching parent tenants:', error);
    } finally {
      setIsLoadingParents(false);
    }
  }, [tenantLevel, parentTenantId, tenant]);

  // Fetch parent tenants based on selected level
  useEffect(() => {
    if (tenantLevel) {
      fetchParentTenants();
    }
  }, [tenantLevel, fetchParentTenants]);

  // Load countries on component mount
  useEffect(() => {
    const loadCountries = async () => {
      if (cachedCountries) {
        setCountries(cachedCountries);
        return;
      }
      setIsLoadingCountries(true);
      try {
        const countriesData = await getCountries();
        cachedCountries = countriesData;
        setCountries(countriesData);
      } catch (error) {
        console.error('Error loading countries:', error);
      } finally {
        setIsLoadingCountries(false);
      }
    };
    
    loadCountries();
  }, []);

  // Load cities when country changes
  useEffect(() => {
    const loadCities = async () => {
      if (!selectedCountry) {
        setCities([]);
        return;
      }

      const selectedCountryData = countries.find(c => c.name === selectedCountry);
      
      if (!selectedCountryData) {
        return;
      }

      setIsLoadingCities(true);
      try {
        const citiesData = await getCitiesForCountry(selectedCountryData.alpha2Code);
        setCities(citiesData);
      } catch (error) {
        console.error('Error loading cities:', error);
      } finally {
        setIsLoadingCities(false);
      }
    };

    loadCities();
  }, [selectedCountry, countries]);

  // Handle manual input mode for head quarter when editing existing tenants
  useEffect(() => {
    if (selectedHeadQuarter && selectedCountry) {
      const selectedCountryData = countries.find(c => c.name === selectedCountry);
      if (selectedCountryData) {
        // Check if the head quarter is in the cities list for this country
        const cityExists = cities.some(city => city.name === selectedHeadQuarter);
        if (!cityExists && selectedHeadQuarter !== 'Other') {
          // If the head quarter is not in the cities list, enable manual input mode
          setIsManualInputMode(true);
        }
      }
    }
  }, [selectedHeadQuarter, selectedCountry, cities, countries]);

  // Load industries on component mount
  useEffect(() => {
    const loadIndustries = async () => {
      if (cachedIndustries) {
        setIndustries(cachedIndustries);
        return;
      }
      setIsLoadingIndustries(true);
      try {
        const industriesData = await getIndustries();
        cachedIndustries = industriesData;
        setIndustries(industriesData);
      } catch (error) {
        console.error('Error loading industries:', error);
      } finally {
        setIsLoadingIndustries(false);
      }
    };
    
    loadIndustries();
  }, []);
  
  // Handle opening the image upload modal
  const handleOpenImageUploadModal = () => {
    setShowImageUploadModal(true);
  };

  // Handle image selection from the modal
  const handleImageSelected = (imageUrl: string) => {
    setLogoPreview(imageUrl);
    setValue('logo_url', imageUrl);
  };
  
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1.5">
            Tenant Name <span className="text-primary">*</span>
          </label>
          <input
            id="name"
            type="text"
            className={formFieldStyle}
            placeholder="Enter tenant name"
            {...register('name')}
          />
          {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name.message}</p>}
        </div>
      </div>
      
      {/* Tenant Code field */}
      <div className="grid grid-cols-1 gap-6">
        <div>
          <label htmlFor="tenant_code" className="block text-sm font-medium text-gray-300 mb-1.5">
            Tenant Code <span className="text-xs text-gray-400">(Optional)</span>
          </label>
          <div>
            <input
              id="tenant_code"
              type="text"
              className={formFieldStyle}
              placeholder="Enter tenant code or leave blank for auto-generation"
              {...register('tenant_code')}
            />
            <p className="text-gray-400 text-xs mt-1">
              If left blank, a code will be automatically generated based on the tenant name.
            </p>
            {errors.tenant_code && <p className="text-red-400 text-xs mt-1">{errors.tenant_code.message}</p>}
          </div>
        </div>
      </div>
      
      {/* Logo upload section */}
      <div className="grid grid-cols-1 gap-6">
        <div>
          <label htmlFor="logo_url" className="block text-sm font-medium text-gray-300 mb-1.5">
            Tenant Logo
          </label>
          <div className="flex items-center gap-4">
            {/* Clickable logo preview/upload area */}
            <div 
              className="relative w-16 h-16 bg-slate-800/50 rounded-md border border-primary/30 overflow-hidden cursor-pointer hover:border-primary/50 transition-colors group"
              onClick={handleOpenImageUploadModal}
            >
            {logoPreview ? (
                <>
                <img 
                  src={logoPreview} 
                  alt="Logo Preview" 
                  className="w-full h-full object-contain"
                  onError={() => setLogoPreview(undefined)}
                />
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <ImagePlus size={20} className="text-white" />
                  </div>
                  {/* Remove button */}
                <button
                  type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                    setLogoPreview(undefined);
                    setValue('logo_url', '');
                  }}
                    className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl-md hover:bg-red-600 transition-colors"
                >
                  <X size={12} />
                </button>
                </>
            ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImagePlus size={24} className="text-gray-400 group-hover:text-primary transition-colors" />
              </div>
            )}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">Click on the logo area to upload an image</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PremiumSelect
          id="tenant_type"
          label="Tenant Type"
          required
          register={register}
          name="tenant_type"
          error={errors.tenant_type}
        >
          <option value="">Select Tenant Type</option>
          <option value="org">Customer</option>
          <option value="partner">Partner</option>
        </PremiumSelect>
        
        <PremiumSelect
          id="level"
          label="Tenant Level"
          required
          register={register}
          name="level"
          error={errors.level}
        >
          <option value="">Select Tenant Level</option>
          <option value="L0">L0</option>
          {(tenantType === 'partner' || tenantLevel === 'L1' || tenantLevel === 'L2') && (
            <>
              <option value="L1">L1</option>
              <option value="L2">L2</option>
            </>
          )}
          {(tenantType === 'org' || tenantLevel === 'L3') && (
            <option value="L3">L3</option>
          )}
        </PremiumSelect>
      </div>
        
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PremiumSelect
          id="parent_tenant_id"
          label="Parent Tenant"
          register={register}
          name="parent_tenant_id"
          error={errors.parent_tenant_id}
          loading={isLoadingParents}
          disabled={tenantLevel === 'L0' || isLoadingParents}
        >
          <option value="">Select Parent Tenant</option>
          {parentTenants.map((tenant) => (
            <option 
              key={tenant.id} 
              value={tenant.id} 
            >
              {tenant.name} ({tenant.level})
            </option>
          ))}
        </PremiumSelect>
        
        <PremiumSelect
          id="status"
          label="Status"
          required
          register={register}
          name="status"
          error={errors.status}
          disabled={!!tenant?.is_deleted}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
        </PremiumSelect>
        {tenant?.is_deleted && (
          <p className="text-amber-400 text-xs mt-1">
            Status cannot be changed for deleted tenants.
          </p>
        )}
      </div>
      
      {/* Image Upload Modal */}
      {showImageUploadModal && (
        <ImageUploadModal
          isOpen={showImageUploadModal}
          onClose={() => setShowImageUploadModal(false)}
          onImageSelected={handleImageSelected}
          currentUrl={logoPreview}
        />
      )}
      
      {/* New dropdown fields for Country, Industry, Head Quarter */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Country Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">
            Country
          </label>
          <div className="relative">
            <select
              {...register('country')}
              className="w-full px-3 py-2 text-sm border rounded-md bg-slate-800 border-gray-600 text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              disabled={isLoadingCountries}
            >
              <option value="">{isLoadingCountries ? 'Loading countries...' : 'Select Country'}</option>
              {countries.map((country) => (
                <option key={country.alpha2Code} value={country.name}>
                  {country.name}
                </option>
              ))}
            </select>
            {isLoadingCountries && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Loader2 size={16} className="animate-spin text-gray-400" />
              </div>
            )}
          </div>
          {errors.country && <p className="text-red-400 text-xs mt-1">{errors.country.message}</p>}
        </div>
        
        {/* Industry Multi-Select */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">
            Industry
          </label>
          <div className="relative">
            <div className="w-full px-3 pt-1.5 pb-2 text-sm border rounded-md bg-slate-800 border-gray-600 text-gray-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary/50 focus-within:border-primary">
              {selectedIndustries.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1.5">
                {selectedIndustries.map((industryName, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-primary/20 text-primary rounded-md"
                  >
                    <span className="truncate max-w-32">{industryName}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newIndustries = selectedIndustries.filter((_, i) => i !== index);
                        setValue('industry', newIndustries);
                      }}
                      className="ml-1 hover:bg-primary/30 rounded-full p-0.5"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              )}
              <select
                onChange={(e) => {
                  if (e.target.value && !selectedIndustries.includes(e.target.value)) {
                    setValue('industry', [...selectedIndustries, e.target.value]);
                    e.target.value = '';
                  }
                }}
                className="w-full bg-transparent text-sm text-gray-300 placeholder-gray-500 outline-none border-0 p-0 h-auto"
                disabled={isLoadingIndustries}
              >
                <option value="">{isLoadingIndustries ? 'Loading industries...' : 'Add Industry'}</option>
                {industries
                  .filter(industry => !selectedIndustries.includes(industry.name))
                  .map((industry) => (
                    <option key={industry.id} value={industry.name}>
                      {industry.name}
                    </option>
                  ))}
              </select>
            </div>
            {isLoadingIndustries && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Loader2 size={16} className="animate-spin text-gray-400" />
              </div>
            )}
          </div>
          {errors.industry && <p className="text-red-400 text-xs mt-1">{errors.industry.message}</p>}
        </div>
        
        {/* Head Quarter Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">
            Head Quarter
          </label>
          <div className="relative">
            <select
              {...register('head_quarter')}
              className="w-full px-3 py-2 text-sm border rounded-md bg-slate-800 border-gray-600 text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              disabled={isLoadingCities || !selectedCountry}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'Other') {
                  setIsManualInputMode(true);
                } else if (value !== '') {
                  setIsManualInputMode(false);
                }
                // Also handle when user selects a different option
                if (value !== 'Other' && value !== '') {
                  setIsManualInputMode(false);
                }
              }}
            >
              <option value="">
                {!selectedCountry ? 'Select Country First' : 
                 isLoadingCities ? 'Loading cities...' : 'Select Head Quarter'}
              </option>
              {cities.map((city) => (
                <option key={city.name} value={city.name}>
                  {city.name}{city.state ? `, ${city.state}` : ''}
                </option>
              ))}
            </select>
            {isLoadingCities && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Loader2 size={16} className="animate-spin text-gray-400" />
              </div>
            )}
          </div>
          
          {/* Manual input for "Other" option */}
          {isManualInputMode && (
            <div className="mt-2 p-3 bg-slate-700/50 border border-primary/30 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse"></div>
                <span className="text-sm font-medium text-primary">Custom City Input</span>
              </div>
              <input
                type="text"
                placeholder="Type your custom city name here..."
                className="w-full px-3 py-2 text-sm border rounded-md bg-slate-800 border-primary text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary shadow-lg"
                value={selectedHeadQuarter === 'Other' ? '' : selectedHeadQuarter || ''}
                onChange={(e) => {
                  const customValue = e.target.value.trim();
                  if (customValue) {
                    setValue('head_quarter', customValue);
                  } else {
                    setValue('head_quarter', 'Other');
                  }
                }}
                onBlur={(e) => {
                  const customValue = e.target.value.trim();
                  if (!customValue) {
                    setValue('head_quarter', 'Other');
                    setIsManualInputMode(false);
                  }
                }}
                autoFocus
                style={{ 
                  borderColor: '#3b82f6',
                  boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
                  backgroundColor: '#1e293b'
                }}
              />
              <p className="text-xs text-primary mt-2 font-medium">
                ✏️ Enter the name of your custom city
              </p>
            </div>
          )}
          
          {errors.head_quarter && <p className="text-red-400 text-xs mt-1">{errors.head_quarter.message}</p>}
        </div>
      </div>
    </div>
  );
};

// Documents upload step
const DocumentsStep = ({ watch, setValue }: WatchableStepComponentProps & { setValue: (name: 'sow_documents', value: SOWDocument[]) => void }) => {
  const sowDocuments = watch('sow_documents');

  const handleDocumentsChange = (documents: SOWDocument[]) => {
    setValue('sow_documents', documents);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start p-4 bg-background-card rounded-lg border border-primary/30">
        <div className="flex items-center">
          <div className="h-12 w-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-lg">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div className="ml-3">
            <h2 className="text-xl font-semibold text-white">Documents</h2>
            <p className="text-sm text-gray-400 mt-1">
              Upload SOW and related tenant documents
            </p>
          </div>
        </div>
      </div>

      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <FileText size={16} className="mr-2 text-primary" />
          SOW &amp; Documents
        </div>
        <div className="p-4">
          <label className="block text-sm font-medium text-gray-300 mb-1.5">
            Documents
          </label>
          <MultipleDocumentUpload
            documents={sowDocuments || []}
            onDocumentsChange={handleDocumentsChange}
            maxFileSize={25}
            maxFiles={5}
          />
        </div>
      </div>
    </div>
  );
};

// Step 2: Dates and Contract Configuration
const DatesContractStep = ({ register, errors }: StepComponentProps) => {
  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex items-start p-4 bg-background-card rounded-lg border border-primary/30">
        <div className="flex items-center">
          <div className="h-12 w-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-lg">
            <Calendar className="h-6 w-6 text-white" />
          </div>
          <div className="ml-3">
            <h2 className="text-xl font-semibold text-white">Dates & Contract</h2>
            <p className="text-sm text-gray-400 mt-1">
              Configure contract dates and thresholds
            </p>
          </div>
        </div>
      </div>

      {/* Form section */}
      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Calendar size={16} className="mr-2 text-primary" />
          Dates & Contract Configuration
        </div>
        <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Start Date */}
        <div className="form-group">
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-300 mb-1.5">
                Start Date
              </label>
          <input
            id="start_date"
            type="date"
                className={formFieldStyle}
            {...register('start_date')}
          />
          {errors.start_date && <p className="text-red-400 text-xs mt-1">{errors.start_date.message}</p>}
        </div>
        
        {/* End Date */}
        <div className="form-group">
              <label htmlFor="end_date" className="block text-sm font-medium text-gray-300 mb-1.5">
                End Date
              </label>
          <input
            id="end_date"
            type="date"
                className={formFieldStyle}
            {...register('end_date')}
          />
          {errors.end_date && <p className="text-red-400 text-xs mt-1">{errors.end_date.message}</p>}
        </div>
        
        {/* Onboarding Date */}
        <div className="form-group">
              <label htmlFor="onboarding_date" className="block text-sm font-medium text-gray-300 mb-1.5">
                Onboarding Date
              </label>
          <input
            id="onboarding_date"
            type="date"
                className={formFieldStyle}
            {...register('onboarding_date')}
          />
          {errors.onboarding_date && <p className="text-red-400 text-xs mt-1">{errors.onboarding_date.message}</p>}
        </div>
        
        {/* Contract Renewal Status */}
        <div className="form-group">
              <label htmlFor="contract_renewal_status" className="block text-sm font-medium text-gray-300 mb-1.5">Contract Renewal Status</label>
          <input
            id="contract_renewal_status"
            type="text"
                className={formFieldStyle}
            placeholder="Enter contract renewal status"
            {...register('contract_renewal_status')}
          />
          {errors.contract_renewal_status && <p className="text-red-400 text-xs mt-1">{errors.contract_renewal_status.message}</p>}
        </div>
        
        {/* Threshold per Day */}
        <div className="form-group">
              <label htmlFor="threshold_per_day" className="block text-sm font-medium text-gray-300 mb-1.5">
                Threshold per Day (GB)
              </label>
          <input
            id="threshold_per_day"
            type="number"
                className={formFieldStyle}
            placeholder="Enter threshold in GB"
            {...register('threshold_per_day')}
          />
          {errors.threshold_per_day && <p className="text-red-400 text-xs mt-1">{errors.threshold_per_day.message}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 3: Retention Configuration
const RetentionStep = ({ register, errors }: StepComponentProps) => {
  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex items-start p-4 bg-background-card rounded-lg border border-primary/30">
        <div className="flex items-center">
          <div className="h-12 w-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-lg">
            <Database className="h-6 w-6 text-white" />
          </div>
          <div className="ml-3">
            <h2 className="text-xl font-semibold text-white">Retention Configuration</h2>
            <p className="text-sm text-gray-400 mt-1">
              Configure data retention periods
            </p>
          </div>
        </div>
      </div>

      {/* Form section */}
      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Database size={16} className="mr-2 text-primary" />
          Retention Periods
        </div>
        <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Online Retention */}
        <div className="form-group">
              <label htmlFor="online_retention" className="block text-sm font-medium text-gray-300 mb-1.5">Online Retention (days)</label>
          <input
            id="online_retention"
            type="number"
                className={formFieldStyle}
            placeholder="Enter online retention period"
            {...register('online_retention')}
          />
          {errors.online_retention && <p className="text-red-400 text-xs mt-1">{errors.online_retention.message}</p>}
        </div>
        
        {/* Offline Retention */}
        <div className="form-group">
              <label htmlFor="offline_retention" className="block text-sm font-medium text-gray-300 mb-1.5">Offline Retention (days)</label>
          <input
            id="offline_retention"
            type="number"
                className={formFieldStyle}
            placeholder="Enter offline retention period"
            {...register('offline_retention')}
          />
          {errors.offline_retention && <p className="text-red-400 text-xs mt-1">{errors.offline_retention.message}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 4: SOAR Configuration
const SoarStep = ({ register, errors, watch }: StepComponentProps) => {
  const [showSoarApiKey, setShowSoarApiKey] = useState(false);
  const [copiedSoarApiKey, setCopiedSoarApiKey] = useState(false);
  const [soarApiKeyValue, setSoarApiKeyValue] = useState('');
  
  // Update SOAR API key value when it changes
  useEffect(() => {
    if (watch) {
      const apiKey = watch('soar_api_key');
      setSoarApiKeyValue(apiKey || '');
    }
  }, [watch]);

  const toggleSoarApiKeyVisibility = () => {
    setShowSoarApiKey(!showSoarApiKey);
  };

  const copySoarApiKey = () => {
    if (soarApiKeyValue) {
      navigator.clipboard.writeText(soarApiKeyValue);
      setCopiedSoarApiKey(true);
      setTimeout(() => setCopiedSoarApiKey(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex items-start p-4 bg-background-card rounded-lg border border-primary/30">
        <div className="flex items-center">
          <div className="h-12 w-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-lg">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div className="ml-3">
            <h2 className="text-xl font-semibold text-white">SOAR Configuration</h2>
            <p className="text-sm text-gray-400 mt-1">
              Configure SOAR integration settings
            </p>
          </div>
        </div>
      </div>
      
      {/* Form section */}
      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Shield size={16} className="mr-2 text-primary" />
          SOAR Integration
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* API URL */}
            <div className="form-group">
              <label htmlFor="soar_api_url" className="block text-sm font-medium text-gray-300 mb-1.5">API URL</label>
              <input
                id="soar_api_url"
                type="text"
                className={formFieldStyle}
                placeholder="Enter API URL"
                {...register('soar_api_url')}
              />
              {errors.soar_api_url && <p className="text-red-400 text-xs mt-1">{errors.soar_api_url.message}</p>}
            </div>
            
            {/* Tenant ID */}
            <div className="form-group">
              <label htmlFor="soar_tenant_id" className="block text-sm font-medium text-gray-300 mb-1.5">Tenant ID</label>
              <input
                id="soar_tenant_id"
                type="text"
                className={formFieldStyle}
                placeholder="Enter tenant ID"
                {...register('soar_tenant_id')}
              />
              {errors.soar_tenant_id && <p className="text-red-400 text-xs mt-1">{errors.soar_tenant_id.message}</p>}
            </div>

            {/* API Key */}
            <div className="form-group md:col-span-2">
              <label htmlFor="soar_api_key" className="block text-sm font-medium text-gray-300 mb-1.5">API Key</label>
              <div className="relative">
                <input
                  id="soar_api_key"
                  type={showSoarApiKey ? "text" : "password"}
                  className={`${formFieldStyle} pr-16`}
                  placeholder="Enter API key"
                  {...register('soar_api_key')}
                  onChange={(e) => setSoarApiKeyValue(e.target.value)}
                  value={soarApiKeyValue}
                />
                <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                  <button
                    type="button"
                    onClick={toggleSoarApiKeyVisibility}
                    className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                    title={showSoarApiKey ? "Hide API key" : "Show API key"}
                  >
                    {showSoarApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  {soarApiKeyValue && (
                    <button
                      type="button"
                      onClick={copySoarApiKey}
                      className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                      title="Copy to clipboard"
                    >
                      {copiedSoarApiKey ? <CheckCheck size={16} className="text-green-400" /> : <Copy size={16} />}
                    </button>
                  )}
                </div>
              </div>
              {errors.soar_api_key && <p className="text-red-400 text-xs mt-1">{errors.soar_api_key.message}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 5: Advisory Configuration
const AdvisoryStep = ({ 
  register, 
  errors, 
  setValue, 
  watch,
  control,
  selectedFromEmail,
  setSelectedFromEmail
}: WatchableStepComponentProps & { 
  setValue: any,
  control: any,
  selectedFromEmail: string,
  setSelectedFromEmail: React.Dispatch<React.SetStateAction<string>>
}) => {
  // Get current email values
  const emailTo = watch('email_to') as string;
  const emailCC = watch('email_cc') as string;
  
  const [showVisionOneApiKey, setShowVisionOneApiKey] = useState(false);
  const [copiedVisionOneApiKey, setCopiedVisionOneApiKey] = useState(false);
  const [visionOneApiKeyValue, setVisionOneApiKeyValue] = useState('');
  const [showEmailConfig, setShowEmailConfig] = useState(false);
  const [emailConfigs, setEmailConfigs] = useState<EmailConfig[]>([]);
  
  const visionOneApiKey = watch('vision_one_api_key');
  
  // Update Vision One API key value when it changes
  useEffect(() => {
    setVisionOneApiKeyValue(visionOneApiKey || '');
  }, [visionOneApiKey]);
  
  // Fetch email configurations on component mount
  useEffect(() => {
    const fetchEmailConfigs = async () => {
      try {
        const response = await getEmailConfigs();
        setEmailConfigs(response.email_configs);
        
        // Prefer existing form value (e.g., tenant.from_email when editing)
        const current = (watch && (watch('from_email') as string)) || '';
        if (current && response.email_configs.some(c => c.email === current)) {
          setSelectedFromEmail(current);
          setValue('from_email', current);
        } else {
          // Otherwise choose default, else first
          const defaultEmail = response.email_configs.find(config => config.is_default);
          if (defaultEmail) {
            setSelectedFromEmail(defaultEmail.email);
            setValue('from_email', defaultEmail.email);
          } else if (response.email_configs.length > 0) {
            setSelectedFromEmail(response.email_configs[0].email);
            setValue('from_email', response.email_configs[0].email);
          }
        }
      } catch (error) {
        console.error('Failed to fetch email configurations:', error);
      }
    };
    
    fetchEmailConfigs();
  }, [setValue, watch, setSelectedFromEmail]);
  
  const toggleVisionOneApiKeyVisibility = () => {
    setShowVisionOneApiKey(!showVisionOneApiKey);
  };

  const copyVisionOneApiKey = () => {
    if (visionOneApiKeyValue) {
      navigator.clipboard.writeText(visionOneApiKeyValue);
      setCopiedVisionOneApiKey(true);
      setTimeout(() => setCopiedVisionOneApiKey(false), 2000);
    }
  };
  
  const handleEmailToChange = (value: string) => {
    setValue('email_to', value);
  };
  
  const handleEmailCCChange = (value: string) => {
    setValue('email_cc', value);
  };
  
  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex items-start p-4 bg-background-card rounded-lg border border-primary/30">
        <div className="flex items-center">
          <div className="h-12 w-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-lg">
            <Mail className="h-6 w-6 text-white" />
          </div>
          <div className="ml-3">
            <h2 className="text-xl font-semibold text-white">Advisory Configuration</h2>
            <p className="text-sm text-gray-400 mt-1">
              Configure email notifications and XDR integration
            </p>
          </div>
        </div>
      </div>

      {/* Form section - Email */}
      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Mail size={16} className="mr-2 text-primary" />
          Email Configuration
        </div>
        <div className="p-4 space-y-6">
          {/* From Email (Dropdown) */}
          <div className="form-group">
            <label htmlFor="from_email" className="form-label flex items-center">
              <Mail size={16} className="mr-2" />
              From Email
            </label>
            <div className="relative">
              <div className="flex items-center">
                <div className="relative flex-grow">
                  {/* Hidden input to store the selected from email */}
                  <input 
                    type="hidden" 
                    {...register('from_email')}
                    value={selectedFromEmail} 
                  />
                  <select
                    id="from_email"
                    className="form-select pr-10 appearance-none"
                    value={selectedFromEmail}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setSelectedFromEmail(newValue);
                      setValue('from_email', newValue);
                    }}
                  >
                    {emailConfigs.length === 0 ? (
                      <option value="">No email configurations available</option>
                    ) : (
                      emailConfigs.map((config) => (
                        <option key={config.uuid} value={config.email}>
                          {config.email} {config.is_default ? '(Default)' : ''}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowEmailConfig(!showEmailConfig);
                  }}
                  className="ml-2 px-3 py-2 bg-primary hover:bg-primary-dark text-white rounded-md text-sm"
                >
                  Configure
                </button>
              </div>
            </div>
          </div>
          
          {/* Email Configuration Panel */}
          {showEmailConfig && (
            <div className="border border-gray-700 rounded-lg p-4 bg-slate-800/30 mb-4">
              <EmailConfigList
                onSelectEmail={(email) => {
                  setSelectedFromEmail(email);
                  setShowEmailConfig(false);
                }}
                insideForm={true}
                onChanged={(configs) => {
                  setEmailConfigs(configs);
                  // If there is a default, prefer it; else keep current selection if still present
                  const defaultCfg = configs.find(c => c.is_default);
                  if (defaultCfg) {
                    setSelectedFromEmail(defaultCfg.email);
                    setValue('from_email', defaultCfg.email);
                  } else if (!configs.find(c => c.email === selectedFromEmail)) {
                    // If current selection got removed, pick first or empty
                    const next = configs[0]?.email || '';
                    setSelectedFromEmail(next);
                    setValue('from_email', next);
                  }
                }}
              />
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Email To */}
        <MultiEmailInput
          id="email_to"
          label="Email To"
          placeholder="Enter primary email address"
          value={emailTo}
          onChange={handleEmailToChange}
          error={errors.email_to}
        />
        
        {/* Email CC */}
        <MultiEmailInput
          id="email_cc"
          label="Email CC"
          placeholder="Enter CC email addresses"
          value={emailCC}
          onChange={handleEmailCCChange}
          error={errors.email_cc}
        />
          </div>
        </div>
      </div>
      
      {/* Form section - XDR */}
      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Shield size={16} className="mr-2 text-primary" />
          XDR Configuration
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* XDR URL */}
        <div className="form-group">
              <label htmlFor="vision_one_url" className="block text-sm font-medium text-gray-300 mb-1.5">XDR URL</label>
          <input
            id="vision_one_url"
            type="text"
                className={formFieldStyle}
            placeholder="Enter XDR URL"
            {...register('vision_one_url')}
          />
          {errors.vision_one_url && <p className="text-red-400 text-xs mt-1">{errors.vision_one_url.message}</p>}
        </div>
        
        {/* XDR API Key */}
        <div className="form-group">
              <label htmlFor="vision_one_api_key" className="block text-sm font-medium text-gray-300 mb-1.5">XDR API Key</label>
              <div className="relative">
                <input
                  id="vision_one_api_key"
                  type={showVisionOneApiKey ? "text" : "password"}
                  className={`${formFieldStyle} pr-16`}
                  placeholder="Enter XDR API key"
                  {...register('vision_one_api_key')}
                  onChange={(e) => setVisionOneApiKeyValue(e.target.value)}
                  value={visionOneApiKeyValue}
                />
                <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                  <button
                    type="button"
                    onClick={toggleVisionOneApiKeyVisibility}
                    className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                    title={showVisionOneApiKey ? "Hide API key" : "Show API key"}
                  >
                    {showVisionOneApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  {visionOneApiKeyValue && (
                    <button
                      type="button"
                      onClick={copyVisionOneApiKey}
                      className="p-1.5 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
                      title="Copy to clipboard"
                    >
                      {copiedVisionOneApiKey ? <CheckCheck size={16} className="text-green-400" /> : <Copy size={16} />}
                    </button>
                  )}
                </div>
              </div>
          {errors.vision_one_api_key && <p className="text-red-400 text-xs mt-1">{errors.vision_one_api_key.message}</p>}
        </div>
        
        {/* Scan Action */}
        <PremiumSelect
          id="scan_action"
          label="Scan Action (Optional)"
          register={register}
          name="scan_action"
          error={errors.scan_action}
        >
          <option value="">None (Optional)</option>
          <option value="logging">Logging</option>
          <option value="block/quarantine">Block/Quarantine</option>
        </PremiumSelect>
          </div>
        </div>
      </div>
       
      {/* Feature flags section */}
      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <ClipboardCheck size={16} className="mr-2 text-primary" />
          Feature Configuration
        </div>
        <div className="p-4 space-y-4">
        <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" id="attached_ioc" className="sr-only peer" {...register('attached_ioc')} />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-gray-400 after:border-gray-400 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary/50 peer-checked:after:bg-primary"></div>
          </label>
            <label htmlFor="attached_ioc" className="block text-sm font-medium text-gray-300">Attached IOC</label>
        </div>
        
        <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" id="masking_ioc" className="sr-only peer" {...register('masking_ioc')} />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-gray-400 after:border-gray-400 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary/50 peer-checked:after:bg-primary"></div>
          </label>
            <label htmlFor="masking_ioc" className="block text-sm font-medium text-gray-300">Masking IOC</label>
        </div>
        
        <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" id="sweeping_on_vision_one" className="sr-only peer" {...register('sweeping_on_vision_one')} />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-gray-400 after:border-gray-400 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary/50 peer-checked:after:bg-primary"></div>
          </label>
            <label htmlFor="sweeping_on_vision_one" className="block text-sm font-medium text-gray-300">Sweeping on XDR</label>
        </div>
        
        <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" id="sweeping_on_esdl" className="sr-only peer" {...register('sweeping_on_esdl')} />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-gray-400 after:border-gray-400 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary/50 peer-checked:after:bg-primary"></div>
          </label>
            <label htmlFor="sweeping_on_esdl" className="block text-sm font-medium text-gray-300">Sweeping on Eventus Platform</label>
          </div>
        </div>
      </div>
      
      {/* Group Configuration section */}
      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Building size={16} className="mr-2 text-primary" />
          Group Configuration
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 gap-6">
            {/* Tenant Group */}
            <PremiumSelect
              id="group"
              label="Group (Optional)"
              register={register}
              name="group"
              error={errors.group}
            >
              <option value="">None (Optional)</option>
              <option value="Eventus">Eventus</option>
              <option value="Eventus UAT">Eventus UAT</option>
              <option value="MMAE">MMAE</option>
              <option value="Blue Ocean">Blue Ocean</option>
              <option value="Insights">Insights</option>
              <option value="Send Ioc">Send IOC</option>
            </PremiumSelect>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 6: Asset Repo Configuration
const AssetManagementStep = ({ 
  register, 
  errors, 
  setValue, 
  watch
}: WatchableStepComponentProps & { 
  setValue: any
}) => {
  const iocs = (watch && watch('asset_management_iocs')) || [];
  
  const [newIoc, setNewIoc] = useState({
    type: '',
    value: '',
    action: ''
  });

  const iocTypes = [
    { value: 'ip', label: 'IP Address' },
    { value: 'domain', label: 'Domain' },
    { value: 'hash', label: 'Hash' },
    { value: 'url', label: 'URL' },
    { value: 'email', label: 'Email' },
    { value: 'hostname', label: 'Hostname' },
    { value: 'username', label: 'Username' },
    { value: 'file_path', label: 'File Path' }
  ];

  const handleAddIoc = () => {
    if (newIoc.type && newIoc.value && newIoc.action) {
      const currentIocs = Array.isArray(iocs) ? iocs : [];
      const updatedIocs = [...currentIocs, { ...newIoc }];
      setValue('asset_management_iocs', updatedIocs);
      setNewIoc({ type: '', value: '', action: '' });
    }
  };

  const handleRemoveIoc = (index: number) => {
    const currentIocs = Array.isArray(iocs) ? iocs : [];
    const updatedIocs = currentIocs.filter((_: any, i: number) => i !== index);
    setValue('asset_management_iocs', updatedIocs);
  };

  const getIocTypeLabel = (type: string) => {
    return iocTypes.find(t => t.value === type)?.label || type;
  };

  const getActionLabel = (action: string) => {
    return action || 'N/A';
  };

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex items-start p-4 bg-background-card rounded-lg border border-primary/30">
        <div className="flex items-center">
          <div className="h-12 w-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-lg">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div className="ml-3">
            <h2 className="text-xl font-semibold text-white">Asset Repo</h2>
            <p className="text-sm text-gray-400 mt-1">
              Configure assets with type, value, and action
            </p>
          </div>
        </div>
      </div>

      {/* Form section - Add New Asset */}
      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Shield size={16} className="mr-2 text-primary" />
          Add New Asset
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Asset Type */}
            <div className="form-group">
              <label htmlFor="ioc_type" className="block text-sm font-medium text-gray-300 mb-1.5">
                Asset Type <span className="text-red-400">*</span>
              </label>
              <select
                id="ioc_type"
                className={formFieldStyle}
                value={newIoc.type}
                onChange={(e) => setNewIoc({ ...newIoc, type: e.target.value })}
                style={selectStyle}
              >
                <option value="">Select Type</option>
                {iocTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Asset Value */}
            <div className="form-group">
              <label htmlFor="ioc_value" className="block text-sm font-medium text-gray-300 mb-1.5">
                Asset Value <span className="text-red-400">*</span>
              </label>
              <input
                id="ioc_value"
                type="text"
                className={formFieldStyle}
                placeholder="Enter asset value"
                value={newIoc.value}
                onChange={(e) => setNewIoc({ ...newIoc, value: e.target.value })}
              />
            </div>

            {/* Tag */}
            <div className="form-group">
              <label htmlFor="ioc_action" className="block text-sm font-medium text-gray-300 mb-1.5">
                Tag <span className="text-red-400">*</span>
              </label>
              <input
                id="ioc_action"
                type="text"
                className={formFieldStyle}
                placeholder="Enter tag (e.g., blocked, whitelist, custom)"
                value={newIoc.action}
                onChange={(e) => setNewIoc({ ...newIoc, action: e.target.value })}
              />
            </div>
          </div>

          {/* Add Button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleAddIoc}
              disabled={!newIoc.type || !newIoc.value || !newIoc.action}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <CheckSquare size={16} />
              Add Asset
            </button>
          </div>
        </div>
      </div>

      {/* Existing Assets List */}
      {Array.isArray(iocs) && iocs.length > 0 && (
        <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
          <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center justify-between">
            <div className="flex items-center">
              <Shield size={16} className="mr-2 text-primary" />
              Configured Assets ({iocs.length})
            </div>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {iocs.map((ioc: any, index: number) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-primary/20"
                >
                  <div className="flex-1 grid grid-cols-3 gap-4">
                    <div>
                      <span className="text-xs text-gray-400">Type</span>
                      <p className="text-white font-medium">{getIocTypeLabel(ioc.type)}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400">Value</span>
                      <p className="text-white font-medium break-all">{ioc.value}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400">Tag</span>
                      <p className="text-white font-medium">
                        {getActionLabel(ioc.action)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveIoc(index)}
                    className="ml-4 p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                title="Remove Asset"
                  >
                    <XCircle size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hidden input for form registration */}
      <input
        type="hidden"
        {...register('asset_management_iocs')}
      />
    </div>
  );
};

// Step 7: IDP Configuration
const IdpStep = ({ register, errors }: StepComponentProps) => {
  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex items-start p-4 bg-background-card rounded-lg border border-primary/30">
        <div className="flex items-center">
          <div className="h-12 w-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-lg">
            <Server className="h-6 w-6 text-white" />
          </div>
          <div className="ml-3">
            <h2 className="text-xl font-semibold text-white">IDP Configuration</h2>
            <p className="text-sm text-gray-400 mt-1">
              Configure Identity Provider settings
            </p>
          </div>
        </div>
      </div>

      {/* Form section */}
      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Server size={16} className="mr-2 text-primary" />
          Identity Provider Settings
        </div>
        <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Provider Name */}
        <div className="form-group">
              <label htmlFor="provider_name" className="block text-sm font-medium text-gray-300 mb-1.5">Provider Name</label>
          <input
            id="provider_name"
            type="text"
                className={formFieldStyle}
            placeholder="Enter provider name"
            {...register('provider_name')}
          />
          {errors.provider_name && <p className="text-red-400 text-xs mt-1">{errors.provider_name.message}</p>}
        </div>
        
        {/* Destination */}
        <div className="form-group">
              <label htmlFor="destination" className="block text-sm font-medium text-gray-300 mb-1.5">Destination</label>
          <input
            id="destination"
            type="text"
                className={formFieldStyle}
            placeholder="Enter destination"
            {...register('destination')}
          />
          {errors.destination && <p className="text-red-400 text-xs mt-1">{errors.destination.message}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 7: Summary
const SummaryStep = ({ watch }: { watch: UseFormWatch<TenantFormSchema> }) => {
  const data = watch();
  
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const formatBoolean = (value?: boolean) => {
    return value ? 'Yes' : 'No';
  };
  
  return (
    <div className="space-y-6">
      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Shield size={16} className="mr-2 text-primary" />
          Basic Information
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.logo_url && (
              <div className="col-span-full mb-2">
                <span className="text-xs text-text-muted-dark block mb-1">Logo</span>
                <div className="flex items-center">
                  <div className="w-16 h-16 rounded-md overflow-hidden border border-gray-700 bg-white flex items-center justify-center">
                    <img 
                      src={data.logo_url} 
                      alt="Tenant Logo" 
                      className="w-full h-full object-contain"
                      onLoad={() => {}}
                      onError={(e) => {
                        console.error("Error loading logo in summary view:", e);
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                  <span className="ml-3 text-xs text-gray-400">
                    {data.logo_url.startsWith('data:') 
                      ? 'Base64 encoded image' 
                      : data.logo_url.substring(0, 30) + '...'}
                  </span>
                </div>
              </div>
            )}
            
            {data.sow_documents && data.sow_documents.length > 0 && (
              <div className="col-span-full mb-2">
                <span className="text-xs text-text-muted-dark block mb-1">Documents</span>
                <div className="space-y-2">
                  {data.sow_documents.map((doc) => (
                    <div key={doc.id} className="flex items-center">
                      <div className="w-10 h-10 rounded-md overflow-hidden border border-gray-700 bg-slate-800/50 flex items-center justify-center">
                        <FileText size={18} className="text-primary" />
                      </div>
                      <div className="ml-3">
                        <span className="text-xs text-gray-400">
                          {doc.name}
                        </span>
                        <div className="mt-0.5 flex space-x-3">
                          <a 
                            href={doc.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:text-primary/80 underline"
                          >
                            View
                          </a>
                          <span className="text-xs text-gray-500">
                            {(doc.size / (1024 * 1024)).toFixed(2)} MB
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <span className="text-xs text-text-muted-dark block">Name</span>
              <span className="text-sm">{data.name || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Tenant Code</span>
              <span className="text-sm">{data.tenant_code || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Tenant Type</span>
              <span className="text-sm">{data.tenant_type || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Tenant Level</span>
              <span className="text-sm">{data.level || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Group</span>
              <span className="text-sm">{data.group || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Status</span>
              <span className="text-sm">{data.status || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Country</span>
              <span className="text-sm">{data.country || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Industry</span>
              <span className="text-sm">{data.industry.join(', ') || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Head Quarter</span>
              <span className="text-sm">{data.head_quarter || 'Not set'}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Calendar size={16} className="mr-2 text-primary" />
          Dates & Contract
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-text-muted-dark block">Start Date</span>
              <span className="text-sm">{formatDate(data.start_date)}</span>
          </div>
            <div>
              <span className="text-xs text-text-muted-dark block">End Date</span>
              <span className="text-sm">{formatDate(data.end_date)}</span>
                </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Onboarding Date</span>
              <span className="text-sm">{formatDate(data.onboarding_date)}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Contract Renewal Status</span>
              <span className="text-sm">{data.contract_renewal_status || 'Not set'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Database size={16} className="mr-2 text-primary" />
          Retention & Usage
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-text-muted-dark block">Threshold per Day (GB)</span>
              <span className="text-sm">{data.threshold_per_day?.toString() || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Online Retention (days)</span>
              <span className="text-sm">{data.online_retention ? `${data.online_retention} days` : 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Offline Retention (days)</span>
              <span className="text-sm">{data.offline_retention ? `${data.offline_retention} days` : 'Not set'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Link size={16} className="mr-2 text-primary" />
          SOAR Configuration
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-text-muted-dark block">API URL</span>
              <span className="text-sm">{data.soar_api_url || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Tenant ID</span>
              <span className="text-sm">{data.soar_tenant_id || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">API Key</span>
              <span className="text-sm">{data.soar_api_key ? '••••••••' : 'Not set'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Mail size={16} className="mr-2 text-primary" />
          Advisory Configuration
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-text-muted-dark block">From Email</span>
              <span className="text-sm">{data.from_email || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Email To</span>
              <span className="text-sm">{data.email_to || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Email CC</span>
              <span className="text-sm">{data.email_cc || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">XDR URL</span>
              <span className="text-sm">{data.vision_one_url || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">XDR API Key</span>
              <span className="text-sm">{data.vision_one_api_key ? '••••••••' : 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Scan Action</span>
              <span className="text-sm">{data.scan_action || 'Not set'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <ClipboardCheck size={16} className="mr-2 text-primary" />
          Feature Flags
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-text-muted-dark block">Attached IOC</span>
              <span className="text-sm">{formatBoolean(data.attached_ioc)}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Masking IOC</span>
              <span className="text-sm">{formatBoolean(data.masking_ioc)}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Sweeping on XDR</span>
              <span className="text-sm">{formatBoolean(data.sweeping_on_vision_one)}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Sweeping on Eventus Platform</span>
              <span className="text-sm">{formatBoolean(data.sweeping_on_esdl)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Shield size={16} className="mr-2 text-primary" />
          Asset Repo Assets
        </div>
        <div className="p-4">
          {data.asset_management_iocs && Array.isArray(data.asset_management_iocs) && data.asset_management_iocs.length > 0 ? (
            <div className="space-y-2">
              {data.asset_management_iocs.map((ioc: any, index: number) => (
                <div key={index} className="p-2 bg-slate-800/50 rounded text-sm">
                  <span className="text-gray-400">Type: </span>
                  <span className="text-white">{ioc.type || 'N/A'}</span>
                  {' | '}
                  <span className="text-gray-400">Value: </span>
                  <span className="text-white">{ioc.value || 'N/A'}</span>
                  {' | '}
                  <span className="text-gray-400">Action: </span>
                  <span className={ioc.action === 'blocked' ? 'text-red-400' : 'text-green-400'}>
                    {ioc.action || 'N/A'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-sm text-gray-400">No assets configured</span>
          )}
        </div>
      </div>

      <div className="border border-primary/30 rounded-lg overflow-hidden bg-background-card">
        <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
          <Server size={16} className="mr-2 text-primary" />
          IDP Configuration
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-text-muted-dark block">Provider Name</span>
              <span className="text-sm">{data.provider_name || 'Not set'}</span>
            </div>
            <div>
              <span className="text-xs text-text-muted-dark block">Destination</span>
              <span className="text-sm">{data.destination || 'Not set'}</span>
            </div>
            </div>
          </div>
      </div>
    </div>
  );
};

// Progress indicator
const ProgressIndicator = ({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) => {
  // Calculate percentage - if we're on the last step, it should be 100%
  const percentage = currentStep === totalSteps - 1 ? 100 : Math.round((currentStep / (totalSteps - 1)) * 100);
  
      // Progress indicator updated
  
  return (
    <div className="mb-6">
      <div className="flex justify-between mb-1.5">
        <span className="text-xs font-medium text-gray-300">Step {currentStep + 1} of {totalSteps}</span>
        <span className="text-xs font-medium text-primary">{percentage}% Complete</span>
      </div>
      <div className="w-full h-2.5 bg-gray-800/80 rounded-full overflow-hidden shadow-inner">
        <div 
          className="h-full bg-gradient-to-r from-primary-600 to-primary-500 rounded-full transition-all duration-300 ease-in-out"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};

// Step navigation
interface StepIndicatorProps {
  steps: { title: string; icon: React.ReactNode }[];
  currentStep: number;
  onStepClick: (step: number) => void;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, currentStep, onStepClick }) => {
  return (
    <div className="flex items-center justify-between mb-8 overflow-x-auto py-3 px-1 -mx-1 custom-scrollbar">
      {steps.map((step, index) => (
        <button
          key={index}
          onClick={() => onStepClick(index)}
          className={`flex flex-col items-center min-w-[85px] px-2 transition-all duration-200 ${
            index === currentStep 
              ? 'cursor-pointer' 
              : index < currentStep
                ? 'cursor-pointer'
                : 'cursor-pointer'
          }`}
          type="button"
        >
          <div className={`flex items-center justify-center w-12 h-12 mb-2 rounded-full shadow-md
            ${currentStep === index 
              ? 'bg-gradient-to-b from-primary-600 to-primary-700 text-white border-2 border-primary/40' 
              : index < currentStep
                ? 'bg-primary-900/20 text-primary-400 border border-primary/30'
                : 'bg-gray-800/80 text-gray-500 border border-gray-700'}`}
          >
            {index < currentStep ? (
              <Check size={18} className="text-primary-400" />
            ) : (
              <span className="text-lg">{step.icon}</span>
            )}
          </div>
          <span className={`text-xs font-medium ${
            currentStep === index 
              ? 'text-white'
              : index < currentStep
                ? 'text-primary-400'
                : 'text-gray-500'
          }`}>{step.title}</span>
        </button>
      ))}
    </div>
  );
};

// Main multi-step form component
interface MultiStepTenantFormProps {
  tenant?: TenantFormContext;
  onSubmit: (data: TenantFormData) => void | Promise<void>;
  isSubmitting?: boolean;
  isEditMode?: boolean;
  initialLevel?: TenantLevel;
  initialType?: TenantType;
}

const MultiStepTenantForm: React.FC<MultiStepTenantFormProps> = ({
  tenant,
  onSubmit,
  isSubmitting: formIsSubmitting = false,
  isEditMode = false,
  initialLevel,
  initialType = TenantType.ORG
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [validationError, setValidationError] = useState('');
  const [selectedFromEmail, setSelectedFromEmail] = useState('');
  
  // Get default level based on initialLevel prop or tenant data
  const getDefaultLevel = () => {
    if (tenant?.level) {
      return tenant.level;
    }
    
    if (initialLevel) {
      return initialLevel;
    }
    
    // If no level is specified, don't set a default
    return '';
  };
  
  // Define the form methods
  const methods = useForm<TenantFormSchema>({
    resolver: zodResolver(tenantFormSchema) as any, // Use type assertion to resolve type mismatch
    defaultValues: {
      name: '',
      tenant_code: '',
      uuid: '',
      tenant_type: initialType || '',
      level: getDefaultLevel(),
      logo_url: '',
      sow_document_url: '',
      sow_documents: [],
      group: '',
      parent_tenant_id: '',
      status: 'active',
      start_date: '',
      end_date: '',
      onboarding_date: '',
      contract_renewal_status: '',
      threshold_per_day: null,
      online_retention: null,
      offline_retention: null,
      email_to: '',
      email_cc: '',
      from_email: (tenant as any)?.from_email || '',
      soar_api_key: '',
      soar_api_url: '',
      soar_tenant_id: '',
      vision_one_url: '',
      vision_one_api_key: '',
      destination: '',
      provider_name: '',
      scan_action: '',
      attached_ioc: false,
      masking_ioc: false,
      sweeping_on_vision_one: false,
      sweeping_on_esdl: false,
      country: '',
      industry: [],
      head_quarter: '',
      asset_management_iocs: []
    },
  });
  
  const { handleSubmit, watch, formState: { errors }, trigger, setValue, register, control } = methods;
  
  // Track level selection changes
  const currentLevel = watch('level');
  useEffect(() => {
    // Level selection updated
  }, [currentLevel]);
  
  // Use tenant data as initial values
  useEffect(() => {
    if (tenant) {
      
      // Helper function to format date for input fields
      const formatDateForInput = (dateValue: any) => {
        if (!dateValue) return '';
        if (typeof dateValue === 'string') {
          // If it's already a string in YYYY-MM-DD format, return as is
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
            return dateValue;
          }
          // If it's a date string, convert to YYYY-MM-DD
          try {
            const date = new Date(dateValue);
            return date.toISOString().split('T')[0];
          } catch (e) {
            return '';
          }
        }
        if (dateValue instanceof Date) {
          return dateValue.toISOString().split('T')[0];
        }
        return '';
      };

      // Set all form values systematically
      const formValues: Partial<TenantFormSchema> = {
        name: tenant.name || '',
        tenant_code: tenant.tenant_code || '',
        uuid: tenant.uuid || '',
        tenant_type: tenant.tenant_type || '',
        level: tenant.level || '',
        logo_url: tenant.logo_url || '',
        sow_document_url: tenant.sow_document_url || '',
        sow_documents: tenant.sow_documents || [],
        group: tenant.group || '',
        parent_tenant_id: tenant.parent_tenant_id || '',
        status: tenant.status || 'active',
        start_date: formatDateForInput(tenant.start_date),
        end_date: formatDateForInput(tenant.end_date),
        onboarding_date: formatDateForInput(tenant.onboarding_date),
        contract_renewal_status: tenant.contract_renewal_status || '',
        threshold_per_day: tenant.threshold_per_day || null,
        online_retention: tenant.online_retention || null,
        offline_retention: tenant.offline_retention || null,
        email_to: tenant.email_to || '',
        email_cc: tenant.email_cc || '',
        soar_api_key: tenant.soar_api_key || '',
        soar_api_url: tenant.soar_api_url || '',
        soar_tenant_id: (tenant as any).soar_tenant_id || '',
        vision_one_url: tenant.vision_one_url || '',
        vision_one_api_key: tenant.vision_one_api_key || '',
        destination: tenant.destination || '',
        provider_name: tenant.provider_name || '',
        scan_action: tenant.scan_action || '',
        attached_ioc: tenant.attached_ioc || false,
        masking_ioc: tenant.masking_ioc || false,
        sweeping_on_vision_one: tenant.sweeping_on_vision_one || false,
        sweeping_on_esdl: tenant.sweeping_on_esdl || false,
        country: tenant.country || '',
        industry: tenant.industry || [],
        head_quarter: tenant.head_quarter || '',
        asset_management_iocs: Array.isArray((tenant as any)?.asset_management_iocs) ? (tenant as any).asset_management_iocs : []
      };

      // Set all values at once
      Object.entries(formValues).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          setValue(key as keyof TenantFormSchema, value);
          // Set form value
        }
      });
    } else {
    // Set initialLevel if provided, but only if no tenant level exists
      if (initialLevel) {
      setValue('level', initialLevel);
    }
    
    // Set tenant type based on initialType, but only if no tenant type exists
      if (initialType === TenantType.PARTNER) {
        setValue('tenant_type', 'partner');
      } else {
        setValue('tenant_type', 'org');
      }
    }
  }, [tenant, initialLevel, initialType, setValue]);
  
  const steps = [
    { 
      title: 'Basic Info', 
      icon: <Shield size={16} />, 
      component: <BasicConfigStep 
        register={register} 
        watch={watch} 
        errors={errors} 
        setValue={setValue} 
        tenant={tenant}
      /> 
    },
    { 
      title: 'Dates', 
      icon: <Calendar size={16} />, 
      component: <DatesContractStep 
        register={register} 
        errors={errors} 
      /> 
    },
    {
      title: 'Documents',
      icon: <FileText size={16} />,
      component: <DocumentsStep
        register={register}
        watch={watch}
        errors={errors}
        setValue={setValue}
      />
    },
    { 
      title: 'Retention', 
      icon: <Database size={16} />, 
      component: <RetentionStep 
        register={register} 
        errors={errors} 
      /> 
    },
    { 
      title: 'SOAR', 
      icon: <Link size={16} />, 
      component: <SoarStep 
        register={register} 
        errors={errors} 
        watch={watch}
      /> 
    },
    { 
      title: 'Advisory', 
      icon: <Mail size={16} />, 
      component: <AdvisoryStep 
        register={register} 
        errors={errors}
        setValue={setValue} 
        watch={watch} 
        control={control}
        selectedFromEmail={selectedFromEmail}
        setSelectedFromEmail={setSelectedFromEmail}
      /> 
    },
    { 
      title: 'Asset Repo', 
      icon: <Shield size={16} />, 
      component: <AssetManagementStep 
        register={register} 
        errors={errors}
        setValue={setValue} 
        watch={watch} 
      /> 
    },
    { 
      title: 'IDP', 
      icon: <Server size={16} />, 
      component: <IdpStep 
        register={register} 
        errors={errors} 
      /> 
    },
    {
      title: 'Review',
      icon: <ClipboardCheck size={16} />,
      component: <SummaryStep watch={watch} />
    }
  ];
  
  const totalSteps = steps.length;
  
  // Required fields for each step
  const stepRequiredFields = [
    ['name', 'tenant_type', 'level', 'status'], // Basic Info
    [], // Dates
    [], // Documents
    [], // Retention
    [], // SOAR
    [], // Advisory
    [], // Asset Repo
    []  // IDP
  ];
  
  const validateAllRequiredFields = async () => {
    setValidationError('');
    
    // Gather all required fields from all steps
    const allRequiredFields = stepRequiredFields.flat();
    
    // Add from_email to validation if it has a value
    if (selectedFromEmail) {
      allRequiredFields.push('from_email');
    }
    
    // Trigger validation for all required fields
    const result = await trigger(allRequiredFields as any);
    
    if (!result) {
      setValidationError('Please fill all required fields before completing the form');
      return false;
    }
    
    return true;
  };
  
  const nextStep = async () => {
    // Get required fields for the current step
    const currentStepRequiredFields = stepRequiredFields[currentStep] || [];
    
    // If there are required fields for this step, validate them
    if (currentStepRequiredFields && currentStepRequiredFields.length > 0) {
      // Trigger validation for current step's required fields
      const result = await trigger(currentStepRequiredFields as any);
      
      if (!result) {
        setValidationError('Please complete all required fields (*) before proceeding');
        return;
      }
    }
    
    // Clear any previous validation errors
    setValidationError('');
    
    // If validation passes or no required fields, proceed to next step
    setCurrentStep(Math.min(currentStep + 1, totalSteps - 1));
    window.scrollTo(0, 0);
  };
  
  const prevStep = () => {
    setCurrentStep(Math.max(currentStep - 1, 0));
    window.scrollTo(0, 0);
  };
  
  const goToStep = (step: number) => {
    // Allow navigation to any step without validation
    setCurrentStep(step);
    window.scrollTo(0, 0);
  };
  
  const submitFormData = async () => {
    const isValid = await validateAllRequiredFields();
    if (!isValid) {
      return;
    }
    await handleSubmit(async (data) => {
      const formattedData = { ...data };
      delete formattedData.uuid;
      await onSubmit(formattedData as unknown as TenantFormData);
    })();
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitFormData();
  };

  const saveFromCurrentStep = async () => {
    setValidationError('');
    await submitFormData();
  };
  
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleFormSubmit} className="w-full">
        <div className="mb-8 px-2">
          <ProgressIndicator currentStep={currentStep} totalSteps={steps.length} />
        
          <StepIndicator 
            steps={steps}
            currentStep={currentStep}
            onStepClick={goToStep}
          />
        </div>
            
            {validationError && (
          <div className="mb-6 p-3 bg-red-900/30 border border-red-800 rounded-lg text-red-400 text-sm">
            {validationError}
              </div>
            )}
            
        <div className="bg-background-card border border-primary/30 rounded-lg overflow-hidden mb-6 shadow-lg">
          <div className="p-5">
            {steps[currentStep].component}
          </div>
        </div>
            
        <div className="flex justify-between mt-8 pt-4 border-t border-primary/20">
          {currentStep > 0 && (
                <button 
                  type="button" 
                  onClick={prevStep}
              className="px-4 py-2 flex items-center text-sm bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors shadow-md border border-primary/10"
                >
              <ArrowLeft size={16} className="mr-2" />
              Previous
                </button>
          )}
          
          {currentStep === 0 && <div />}
          
          {currentStep < steps.length - 1 ? (
            <div className="ml-auto flex gap-3">
                    {currentStep > 0 && stepRequiredFields[currentStep] && stepRequiredFields[currentStep].length === 0 && (
                      <button 
                        type="button" 
                        onClick={nextStep}
                  className="px-4 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors shadow-md border border-primary/10"
                      >
                        Skip
                      </button>
                    )}
                    {isEditMode && (
                      <button
                        type="button"
                        onClick={saveFromCurrentStep}
                        disabled={formIsSubmitting}
                        className="px-4 py-2 flex items-center text-sm bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg transition-colors shadow-md border border-primary/30 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {formIsSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            Save
                            <Check size={16} className="ml-2" />
                          </>
                        )}
                      </button>
                    )}
                    <button 
                      type="button" 
                      onClick={nextStep}
                className="px-4 py-2 flex items-center text-sm bg-gradient-to-r from-primary to-primary-600 hover:from-primary-600 hover:to-primary-500 text-white rounded-lg transition-all shadow-md"
                    >
                Continue
                <ArrowRight size={16} className="ml-2" />
                    </button>
            </div>
                ) : (
                  <button 
                    type="submit"
              disabled={formIsSubmitting}
                    className="ml-auto px-4 py-2.5 flex items-center text-sm bg-gradient-to-r from-primary to-primary-600 hover:from-primary-600 hover:to-primary-500 text-white rounded-lg transition-all shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
                    onClick={(e) => {
                      // Add visual feedback when the button is clicked
                      // Save Tenant button clicked
                      const button = e.currentTarget;
                      button.classList.add("ring-4", "ring-primary/30");
                      
                      // Remove visual feedback after a delay
                      setTimeout(() => {
                        button.classList.remove("ring-4", "ring-primary/30");
                      }, 300);
                    }}
                  >
              {formIsSubmitting ? (
                      <>
                  <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></div>
                  Processing...
                      </>
                    ) : (
                      <>
                  Save Tenant
                  <Check size={16} className="ml-2" />
                      </>
                    )}
                  </button>
                )}
        </div>
      </form>
    </FormProvider>
  );
};

export default MultiStepTenantForm; 